import * as React from 'react';
import {useEffect, useMemo, useState} from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import {alpha, useTheme} from '@mui/material/styles';
import {useLocation, useNavigate} from 'react-router-dom';
import {LogoIcon} from 'src/components/Icons/menu';
import type {Menu, NavSectionId} from 'src/pages/Layout/menu';
import {SidebarSection} from './SidebarSection';
import {SidebarNavItem} from './SidebarNavItem';
import {SidebarFooter} from './SidebarFooter';
import {FrontDeskIntroPopover} from './FrontDeskIntroPopover';

const SETUP_HINT_KEY = 'entrio_sidebar_setup_hint_dismissed';

const SECTION_ORDER: {id: NavSectionId; label: string}[] = [
  {id: 'setup', label: 'SETUP'},
  {id: 'operations', label: 'OPERATIONS'},
  {id: 'system', label: 'SYSTEM'},
];

type Props = {
  open: boolean;
  onToggle: () => void;
  filteredMenus: Menu[];
  DrawerContext: React.Context<boolean>;
};

export function AppSidebar({open, onToggle, filteredMenus, DrawerContext}: Props) {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  const dashboardMenu = useMemo(
    () => filteredMenus.find(m => m.path === '/dashboard'),
    [filteredMenus]
  );

  const menusBySection = useMemo(() => {
    const map: Record<NavSectionId, Menu[]> = {
      setup: [],
      operations: [],
      system: [],
    };
    filteredMenus.forEach(m => {
      if (m.section && map[m.section]) {
        map[m.section].push(m);
      }
    });
    return map;
  }, [filteredMenus]);

  const [frontDeskAnchor, setFrontDeskAnchor] = useState<HTMLDivElement | null>(null);
  const hasFrontDesk = filteredMenus.some(menu => menu.path === '/front-desk');

  const [showSetupHint, setShowSetupHint] = useState(
    () => typeof localStorage !== 'undefined' && localStorage.getItem(SETUP_HINT_KEY) !== '1'
  );

  useEffect(() => {
    const setupPaths = ['/property', '/tenant', '/devices'];
    if (setupPaths.some(p => location.pathname.startsWith(p))) {
      localStorage.setItem(SETUP_HINT_KEY, '1');
      setShowSetupHint(false);
    }
  }, [location.pathname]);

  const dismissSetupHint = (e: React.MouseEvent) => {
    e.stopPropagation();
    localStorage.setItem(SETUP_HINT_KEY, '1');
    setShowSetupHint(false);
  };

  const setupHintNode =
    open && showSetupHint && menusBySection.setup.length > 0 ? (
      <Box
        component="button"
        type="button"
        onClick={dismissSetupHint}
        sx={{
          border: 'none',
          cursor: 'pointer',
          fontSize: '0.625rem',
          fontWeight: 700,
          letterSpacing: '0.04em',
          textTransform: 'uppercase',
          px: 0.75,
          py: 0.35,
          borderRadius: 999,
          lineHeight: 1,
          bgcolor: alpha(theme.palette.primary.main, 0.1),
          color: 'primary.main',
          transition: theme.transitions.create(['background-color', 'transform'], {duration: 180}),
          '&:hover': {
            bgcolor: alpha(theme.palette.primary.main, 0.16),
          },
        }}
      >
        Start here
      </Box>
    ) : null;

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        bgcolor: '#ffffff',
      }}
    >
      {/* Brand */}
      <Box
        sx={{
          flexShrink: 0,
          px: open ? 1.25 : 0.5,
          py: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          borderBottom: `1px solid ${alpha(theme.palette.divider, 0.85)}`,
        }}
      >
        <Box sx={{display: 'flex', alignItems: 'center', minWidth: 0, flex: 1}}>
          <IconButton
            onClick={() => {
              if (!open) {
                onToggle();
              } else {
                navigate('/dashboard');
              }
            }}
            sx={{
              p: 0.75,
              borderRadius: 2,
              color: 'primary.main',
              '&:hover': {bgcolor: alpha(theme.palette.text.primary, 0.04)},
            }}
            aria-label={open ? 'Go to dashboard' : 'Expand sidebar'}
          >
            <LogoIcon sx={{fontSize: 36}} />
          </IconButton>
          {open && (
            <Box sx={{ml: 1, minWidth: 0}}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '1.05rem',
                  letterSpacing: '-0.03em',
                  color: 'text.primary',
                  lineHeight: 1.2,
                }}
              >
                Entrio
              </Typography>
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  fontSize: '0.65rem',
                  letterSpacing: '0.06em',
                  textTransform: 'uppercase',
                  color: 'text.secondary',
                  fontWeight: 600,
                  mt: 0.25,
                  opacity: 0.85,
                }}
              >
                Access & community
              </Typography>
            </Box>
          )}
        </Box>
        {open && (
          <IconButton
            size="small"
            onClick={onToggle}
            aria-label="Collapse sidebar"
            sx={{
              color: 'text.secondary',
              '&:hover': {color: 'text.primary', bgcolor: alpha(theme.palette.text.primary, 0.06)},
            }}
          >
            <ChevronLeftIcon sx={{fontSize: 18}} />
          </IconButton>
        )}
      </Box>

      {/* Scrollable nav */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          px: open ? 1.25 : 0.75,
          py: 2,
          ...(!open && {
            '&::-webkit-scrollbar': {display: 'none'},
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
          }),
        }}
      >
        {/* Dashboard — visually separated */}
        {dashboardMenu && (
          <Box
            sx={{
              mb: open ? 2.5 : 1,
              pb: open ? 2 : 0,
              borderBottom: open ? `1px solid ${alpha(theme.palette.divider, 0.75)}` : 'none',
            }}
          >
            <SidebarNavItem menu={dashboardMenu} open={open} depth={0} />
          </Box>
        )}

        {SECTION_ORDER.map(({id, label}) => {
          const items = menusBySection[id];
          if (!items.length) return null;

          return (
            <SidebarSection
              key={id}
              label={label}
              open={open}
              setupHint={id === 'setup' ? setupHintNode : undefined}
            >
              {items.map(menu => (
                <SidebarNavItem
                  key={menu.title}
                  menu={menu}
                  open={open}
                  depth={0}
                  itemRef={menu.path === '/front-desk' ? setFrontDeskAnchor : undefined}
                />
              ))}
            </SidebarSection>
          );
        })}
      </Box>

      <SidebarFooter DrawerContext={DrawerContext} />

      <FrontDeskIntroPopover anchorEl={frontDeskAnchor} enabled={hasFrontDesk} />
    </Box>
  );
}
